// Map over a list with BOUNDED concurrency, keeping order, and say when the
// first few are ready (Mail speed, Karan 06/10/2026: "it takes ages to load
// mail").
//
// Mail used to hydrate fifty threads in serial chunks of ten: five Gmail round
// trips, one after another, before anything painted. A sliding window of ten
// keeps the same ceiling on concurrent calls (Gmail's per-user quota trips on
// 40+ at once) but never waits for the slowest call in a chunk, and onFirst
// lets the screen draw the top of the inbox while the rest is still arriving.
//
// Pure apart from the promises it is given — tested in node.

/**
 * @param items   the inputs
 * @param fn      async (item, index) => value
 * @param limit   most calls in flight at once
 * @param first   how many leading results onFirst waits for
 * @param onFirst called ONCE with the first `first` results, in order, as soon
 *                as they are all done — before the whole list finishes
 * @returns the results in input order; a failed item is dropped. If EVERY item
 *          fails, it rejects with the first error, so an expired token still
 *          reaches the caller as a failure rather than an empty inbox.
 */
export function mapStream(items, fn, { limit = 10, first = 15, onFirst = null } = {}) {
  const n = items.length;
  if (!n) return Promise.resolve([]);
  return new Promise((resolve, reject) => {
    const out = new Array(n);
    const ok = new Array(n).fill(false);
    const done = new Array(n).fill(false);
    const firstN = Math.min(Math.max(0, first), n);
    let next = 0; let running = 0; let finished = 0; let sent = !onFirst || firstN === 0; let firstError = null;

    const maybeFirst = () => {
      if (sent) return;
      for (let i = 0; i < firstN; i++) if (!done[i]) return;
      sent = true;
      try { onFirst(out.slice(0, firstN).filter((_, i) => ok[i])); } catch (_) { /* the caller's paint must not stop the load */ }
    };
    const pump = () => {
      if (finished === n) {
        if (!ok.some(Boolean)) { reject(firstError || new Error('Nothing could be read')); return; }
        resolve(out.filter((_, i) => ok[i]));
        return;
      }
      while (running < limit && next < n) {
        const i = next++;
        running++;
        Promise.resolve()
          .then(() => fn(items[i], i))
          .then((v) => { out[i] = v; ok[i] = true; }, (e) => { if (!firstError) firstError = e; })
          .then(() => { done[i] = true; running--; finished++; maybeFirst(); pump(); });
      }
    };
    pump();
  });
}
